import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    package: ['M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'],
    alertCircle: ['M12 8v4 M12 16h.01 M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z'],
    tool: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
};

export default function SpareParts({ user, operatorDetails }) {
    const [parts, setParts] = useState([]);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editPart, setEditPart] = useState(null);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        name: '',
        code: '',
        quantity: '',
        unitCost: '',
        minLevel: ''
    });

    useEffect(() => {
        // Fetch from backend in a real app
        setParts([]);
    }, []);

    const stats = useMemo(() => {
        const outOfStock = parts.filter(p => p.quantity <= 0).length;
        const lowStock = parts.filter(p => p.quantity > 0 && p.quantity <= p.minLevel).length;
        const totalValue = parts.reduce((acc, p) => acc + (p.quantity * p.unitCost), 0);
        return { total: parts.length, lowStock, outOfStock, totalValue };
    }, [parts]);

    const filtered = parts.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.code.toLowerCase().includes(search.toLowerCase())
    );

    const handleEdit = (part) => {
        setEditPart(part);
        setForm({ ...part });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to remove this part from inventory?')) {
            setParts(parts.filter(p => p._id !== id));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        const payload = {
            ...form,
            quantity: parseInt(form.quantity) || 0,
            unitCost: parseFloat(form.unitCost) || 0,
            minLevel: parseInt(form.minLevel) || 0
        };
        setTimeout(() => {
            if (editPart) {
                setParts(parts.map(p => p._id === editPart._id ? { ...payload, _id: p._id, lastUpdated: 'Just now' } : p));
            } else {
                setParts([{ ...payload, _id: Date.now().toString(), lastUpdated: 'Just now' }, ...parts]);
            }
            setShowModal(false);
            setEditPart(null);
            setForm({ name: '', code: '', quantity: '', unitCost: '', minLevel: '' });
            setLoading(false);
        }, 800);
    };

    const getStatus = (part) => {
        if (part.quantity <= 0) return { label: 'Out of Stock', color: '#ef4444', bg: '#fef2f2', border: '#fee2e2' };
        if (part.quantity <= part.minLevel) return { label: 'Low Stock', color: '#f59e0b', bg: '#fefce8', border: '#fef3c7' };
        return { label: 'In Stock', color: '#10b981', bg: '#ecfdf5', border: '#d1fae5' };
    };

    return (
        <div className="sp-page">
            <style>{`
                .sp-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .sp-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .sp-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .sp-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .sp-add-btn {
                    background: #2563eb; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(37,99,235,0.2);
                }

                .sp-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 24px; }
                .sp-stat-card {
                    background: white; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px;
                }
                .sp-stat-icon {
                    width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
                }
                .sp-stat-info { display: flex; flex-direction: column; }
                .sp-stat-label { font-size: 12px; font-weight: 600; color: #94a3b8; text-transform: uppercase; }
                .sp-stat-val { font-size: 20px; font-weight: 800; color: #1e293b; }

                .sp-toolbar { margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
                .sp-search-wrap { position: relative; width: 350px; }
                .sp-search-wrap svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .sp-search-input {
                    width: 100%; padding: 10px 14px 10px 38px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    font-size: 14px; outline: none; transition: all 0.2s;
                }
                .sp-search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.05); }

                .sp-card { 
                    background: white; border-radius: 12px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); overflow: hidden;
                }
                .sp-table { width: 100%; border-collapse: collapse; }
                .sp-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 600; 
                    color: #94a3b8; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
                }
                .sp-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .sp-table tr:hover { background: #fafbfc; }

                .sp-name-cell { font-weight: 700; color: #1e293b; display: flex; align-items: center; gap: 10px; }
                .sp-code-badge { background: #f1f5f9; color: #64748b; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; font-family: monospace; }
                
                .sp-status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                    border: 1px solid transparent;
                }

                .sp-actions { display: flex; gap: 8px; }
                .sp-act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .sp-act-btn:hover { background: #f1f5f9; color: #2563eb; }
                .sp-act-btn.del:hover { background: #fef2f2; color: #ef4444; }

                /* Modal */
                .sp-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .sp-modal { background: white; width: 550px; border-radius: 20px; padding: 30px; position: relative; }
                .sp-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .sp-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                
                .sp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
                .sp-form-group { display: flex; flex-direction: column; gap: 6px; }
                .sp-form-group.full { grid-column: span 2; }
                .sp-label { font-size: 12px; font-weight: 700; color: #475569; }
                .sp-input {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s; background: #fff;
                }
                .sp-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
                
                .sp-save-btn {
                    width: 100%; background: #2563eb; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; margin-top: 10px;
                }
            `}</style>

            <div className="sp-header">
                <div>
                    <h1 className="sp-title">Spare Parts Inventory</h1>
                    <p className="sp-subtitle">Track stock levels, costs, and reorder points for maintenance parts.</p>
                </div>
                <button className="sp-add-btn" onClick={() => { setEditPart(null); setShowModal(true); }}>
                    <Icon d={ic.plus} size={18} strokeWidth={2.5} /> Add Spare Part
                </button>
            </div>

            <div className="sp-stats">
                <div className="sp-stat-card">
                    <div className="sp-stat-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                        <Icon d={ic.package} size={20} />
                    </div>
                    <div className="sp-stat-info">
                        <span className="sp-stat-label">Total Parts</span>
                        <span className="sp-stat-val">{stats.total}</span>
                    </div>
                </div>
                <div className="sp-stat-card">
                    <div className="sp-stat-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                        <Icon d={ic.alertCircle} size={20} />
                    </div>
                    <div className="sp-stat-info">
                        <span className="sp-stat-label">Low Stock</span>
                        <span className="sp-stat-val">{stats.lowStock}</span>
                    </div>
                </div>
                <div className="sp-stat-card">
                    <div className="sp-stat-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
                        <Icon d={ic.alertCircle} size={20} />
                    </div>
                    <div className="sp-stat-info">
                        <span className="sp-stat-label">Out of Stock</span>
                        <span className="sp-stat-val">{stats.outOfStock}</span>
                    </div>
                </div>
                <div className="sp-stat-card">
                    <div className="sp-stat-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                        <Icon d={ic.tool} size={20} />
                    </div>
                    <div className="sp-stat-info">
                        <span className="sp-stat-label">Asset Value</span>
                        <span className="sp-stat-val">{formatCurrency(stats.totalValue, operatorDetails?.country)}</span>
                    </div>
                </div>
            </div>

            <div className="sp-toolbar">
                <div className="sp-search-wrap">
                    <Icon d={ic.search} size={16} />
                    <input
                        type="text"
                        placeholder="Search parts by name or SKU..."
                        className="sp-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="sp-card">
                <table className="sp-table">
                    <thead>
                        <tr>
                            <th>Part Name</th>
                            <th>Code / SKU</th>
                            <th>In Stock</th>
                            <th>Unit Cost</th>
                            <th>Status</th>
                            <th>Last Updated</th>
                            <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(part => {
                            const status = getStatus(part);
                            return (
                                <tr key={part._id}>
                                    <td><div className="sp-name-cell">{part.name}</div></td>
                                    <td><span className="sp-code-badge">{part.code}</span></td>
                                    <td>
                                        <div style={{ fontWeight: 700 }}>{part.quantity}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Min: {part.minLevel}</div>
                                    </td>
                                    <td style={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(part.unitCost, operatorDetails?.country)}</td>
                                    <td>
                                        <span className="sp-status-badge" style={{ background: status.bg, color: status.color, borderColor: status.border }}>
                                            {status.label}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: 13, color: '#94a3b8' }}>{part.lastUpdated}</td>
                                    <td>
                                        <div className="sp-actions">
                                            <button className="sp-act-btn" onClick={() => handleEdit(part)}>
                                                <Icon d={ic.edit} size={16} />
                                            </button>
                                            <button className="sp-act-btn del" onClick={() => handleDelete(part._id)}>
                                                <Icon d={ic.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="sp-modal-overlay">
                    <div className="sp-modal">
                        <div className="sp-modal-close" onClick={() => setShowModal(false)}>
                            <Icon d={ic.x} size={20} />
                        </div>
                        <h2 className="sp-modal-title">{editPart ? 'Update Spare Part' : 'Add New Spare Part'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="sp-form-grid">
                                <div className="sp-form-group full">
                                    <label className="sp-label">Part Name *</label>
                                    <input
                                        type="text" className="sp-input" required
                                        placeholder="e.g., Oil Filter, Brake Pads"
                                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    />
                                </div>
                                <div className="sp-form-group">
                                    <label className="sp-label">Part Code / SKU</label>
                                    <input
                                        type="text" className="sp-input"
                                        placeholder="e.g., OF-2023-X"
                                        value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                                    />
                                </div>
                                <div className="sp-form-group">
                                    <label className="sp-label">Stock Quantity *</label>
                                    <input
                                        type="number" className="sp-input" required
                                        value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                                    />
                                </div>
                                <div className="sp-form-group">
                                    <label className="sp-label">Unit Cost ({operatorDetails?.defaultCurrency || 'USD'}) *</label>
                                    <input
                                        type="number" step="0.001" className="sp-input" required
                                        value={form.unitCost} onChange={(e) => setForm({ ...form, unitCost: e.target.value })}
                                    />
                                </div>
                                <div className="sp-form-group">
                                    <label className="sp-label">Reorder Level (Min)</label>
                                    <input
                                        type="number" className="sp-input"
                                        value={form.minLevel} onChange={(e) => setForm({ ...form, minLevel: e.target.value })}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="sp-save-btn" disabled={loading}>
                                {loading ? 'Processing...' : (editPart ? 'Update Part' : 'Add Part')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
